<template>
    <a
        v-if="banner.display_modal && skipProductModal && ctaUrl"
        ref="bannerRoot"
        :href="ctaUrl"
        class="block h-full w-full cursor-pointer"
        :data-banner-id="banner.banner_id"
        @click="trackBannerSelect"
    >
        <slot />
    </a>

    <div
        v-else-if="banner.display_modal && !skipProductModal"
        ref="bannerRoot"
        class="block h-full w-full cursor-pointer"
        role="button"
        tabindex="0"
        :data-banner-id="banner.banner_id"
        @click="openModal"
        @keyup.enter="openModal"
        @keyup.space.prevent="openModal"
    >
        <slot />
    </div>

    <a
        v-else-if="banner.url"
        ref="bannerRoot"
        :href="banner.url"
        class="block h-full w-full cursor-pointer"
        :data-banner-id="banner.banner_id"
        @click="trackBannerSelect"
    >
        <slot />
    </a>

    <div
        v-else
        ref="bannerRoot"
        class="block h-full w-full"
        :data-banner-id="banner.banner_id"
    >
        <slot />
    </div>

    <BannerProductsModal
        v-if="showModal && !skipProductModal"
        :banner-id="banner.banner_id"
        :banner="banner"
        :gtm-creative-slot="gtmCreativeSlot"
        :initial-heading="banner.modal_heading"
        :initial-cta-url="banner.modal_cta_url"
        :initial-cta-text="banner.modal_cta_text"
        @close="closeModal"
    />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import BannerProductsModal from './BannerProductsModal.vue'
import GtmEvent from '@modules/GtmEvent.js'
import { bannerPromotionGrouping } from '@modules/bannerGtm.js'

const props = defineProps({
    banner: {
        type: Object,
        required: true,
    },
    currentCategoryId: {
        type: Number,
        default: null,
    },
    skipProductModal: {
        type: Boolean,
        default: false,
    },
    /**
     * Display surface for GTM creative_slot (e.g. homepage_left, basket, category_banner).
     */
    gtmCreativeSlot: {
        type: String,
        default: null,
    },
})

const page = usePage()

const showModal = ref(false)
const bannerRoot = ref(null)
const hasTrackedBannerView = ref(false)
let bannerVisibilityObserver = null

const ctaUrl = computed(() => props.banner.modal_cta_url || props.banner.url || null)

const promotionGrouping = computed(() => bannerPromotionGrouping(
    props.banner,
    props.gtmCreativeSlot
))

/** Same shape as view_item_list — set on shop category pages only. */
const categoryListDetails = computed(() => page.props.currentCategory || null)

function trackBannerView() {
    if (hasTrackedBannerView.value || ! promotionGrouping.value) {
        return
    }

    hasTrackedBannerView.value = true

    new GtmEvent({
        eventName: 'view_promotion',
        valueRequired: false,
        categoryListDetails: categoryListDetails.value,
    }).trackPromotionCreativeEvent(promotionGrouping.value)
}

function trackBannerSelect() {
    if (! promotionGrouping.value) {
        return
    }

    new GtmEvent({
        eventName: 'select_promotion',
        valueRequired: false,
        categoryListDetails: categoryListDetails.value,
    }).trackPromotionCreativeEvent(promotionGrouping.value)
}

function openModal() {
    trackBannerSelect()
    showModal.value = true
}

function closeModal() {
    showModal.value = false
}

onMounted(() => {
    if (! promotionGrouping.value || ! bannerRoot.value) {
        return
    }

    if (typeof IntersectionObserver === 'undefined') {
        trackBannerView()
        return
    }

    bannerVisibilityObserver = new IntersectionObserver(
        (observerEntries) => {
            const isBannerVisible = observerEntries.some(
                (observerEntry) => observerEntry.isIntersecting
            )

            if (isBannerVisible) {
                trackBannerView()
                bannerVisibilityObserver?.disconnect()
            }
        },
        {
            threshold: 0.35,
        }
    )

    bannerVisibilityObserver.observe(bannerRoot.value)
})

onBeforeUnmount(() => {
    bannerVisibilityObserver?.disconnect()
})
</script>
